/** @jsxImportSource @emotion/react */
import {
  Button,
  Container,
  Grid,
  Icon,
  Input,
  Message,
  RadioGroup,
  Stack,
  Text,
  useBasisFormContext,
  useWatch,
  VisuallyHidden,
} from "basis";
import { Separator } from "components/Separator/Separator";
import { Account, YourAccountsState } from "../reducer/types";
import {
  accountTypeOptionsAu,
  accountTypeOptionsNz,
  validateMonthlyAmount,
} from "./yourAccountsForm";

const isSimilarToCreditCard = ({ accountNumber, accountType }: Account) =>
  accountType === "credit-card" &&
  accountNumber.length === 16 &&
  (accountNumber.startsWith("5") || accountNumber.startsWith("4"));

interface AccountBoxProps {
  index: number;
  region: string;
  onRemove: (index: number) => void;
  account: Account;
}

export const AccountBox = ({
  index,
  region,
  account,
  onRemove,
}: AccountBoxProps) => {
  const { Field, methods } = useBasisFormContext<YourAccountsState>();
  const { control } = methods;
  const currentAccount = useWatch({
    control,
    name: `accounts.${index}` as const,
  });

  return (
    <Container padding="5" hasBorder>
      <Stack gap="4">
        <div
          css={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text as="h3" textStyle="subtitle2">
            <strong>{`Account ${index + 1}`}</strong>
          </Text>
          {index > 0 && (
            <Button variant="icon" onClick={() => onRemove(index)}>
              <Icon name="trash" />
              <VisuallyHidden>
                <span>Remove account {index + 1}</span>
              </VisuallyHidden>
            </Button>
          )}
        </div>
        <Separator />
        <Grid cols={1} cols-sm={2} colsGap-sm="4" rowsGap="8">
          <Grid.Item colSpan="all" rowSpan-sm="0">
            <Field
              name={`accounts.${index}.accountType` as const}
              label="Account type"
              options={
                region === "NZ" ? accountTypeOptionsNz : accountTypeOptionsAu
              }
              columns={2}
              defaultValue={account.accountType}
              as={RadioGroup}
            />
          </Grid.Item>
          <Grid.Item colSpan="all" rowSpan-sm="1">
            <Grid cols={1} cols-sm={2} colsGap-sm="4" rowsGap="2">
              <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="0">
                <Field
                  name={`accounts.${index}.accountNumber` as const}
                  variant="numeric"
                  label="Account number"
                  defaultValue={account.accountNumber}
                  as={Input}
                />
              </Grid.Item>
              {isSimilarToCreditCard(currentAccount) && (
                <Grid.Item colSpan="all" rowSpan-sm="1">
                  <Message
                    severity="info-or-minor"
                    bg="secondary.lightBlue.t25"
                  >
                    This looks similar to a credit card number. Please ensure
                    you are entering your <strong>account number</strong>, which
                    you can find on your statement.
                  </Message>
                </Grid.Item>
              )}
            </Grid>
          </Grid.Item>
          <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="2">
            <Field
              name={`accounts.${index}.monthlyRepayments` as const}
              variant="decimal"
              prefix="$"
              label="How much are you able to afford for this account on a monthly basis?"
              helpText="Minimum amount of $10"
              defaultValue={account.monthlyRepayments}
              validate={validateMonthlyAmount}
              as={Input}
            />
          </Grid.Item>
        </Grid>
      </Stack>
    </Container>
  );
};
